/**
 * 后台 - 视频 Adapter 管理
 *
 * 列出所有已注册的视频 provider（adapter），展示能力矩阵，并允许管理员把
 * 暂不希望使用的 adapter 隐藏掉（不会影响已配置的渠道继续运行）。
 *
 * 数据源：GET /api/video-models/providers/meta（含 isHidden 字段）
 * 操作：PATCH /api/video-models/providers/:id/state
 *
 * 隐藏语义说明：
 * - 影响：管理后台「新建渠道」时下拉只显示未隐藏的 provider
 * - 不影响：已使用该 provider 的渠道继续生效；编辑既有渠道时仍可看到
 */

import { useEffect, useMemo, useState } from 'react';
import { Eye, EyeOff, Info, Loader2, X } from 'lucide-react';
import {
  videoApi,
  type VideoProviderMetaInfo,
} from '../services/videoApi';
import { videoProviderAdminApi } from '../services/videoAdminApi';
import toast from '../../../components/Toast';

export default function VideoProvidersTab() {
  const [providers, setProviders] = useState<VideoProviderMetaInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [detailProvider, setDetailProvider] = useState<VideoProviderMetaInfo | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await videoApi.getProviderMetadata();
      setProviders(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '加载 Adapter 列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const visibleCount = useMemo(
    () => providers.filter((p) => !p.isHidden).length,
    [providers]
  );

  const handleToggleHidden = async (provider: VideoProviderMetaInfo) => {
    const next = !provider.isHidden;
    setSavingId(provider.id);
    try {
      await videoProviderAdminApi.setHidden(provider.id, next);
      setProviders((prev) =>
        prev.map((p) => (p.id === provider.id ? { ...p, isHidden: next } : p))
      );
      toast.success(next ? '已隐藏，不再出现在新建渠道选项中' : '已恢复显示');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '更新失败');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-light-text">视频 Adapter 管理</h2>
          <p className="text-sm text-light-muted mt-1">
            管理已注册的视频上游 adapter，可隐藏暂不希望使用的 adapter
          </p>
        </div>
        <div className="text-sm text-light-muted">
          共 {providers.length} 个，可见 {visibleCount} 个
        </div>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        隐藏只影响「新建渠道」下拉选项与本页徽章。已经配置过该 adapter 的视频渠道
        会继续正常工作，不会被强制下线。
      </div>

      {loading ? (
        <div className="py-12 text-center text-light-muted">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          加载中
        </div>
      ) : providers.length === 0 ? (
        <div className="py-12 text-center text-light-muted text-sm">
          没有注册的视频 Adapter
        </div>
      ) : (
        <div className="bg-white border border-light-border rounded-2xl overflow-hidden">
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full divide-y divide-light-border text-sm">
              <thead className="bg-light-hover">
                <tr className="text-left text-xs uppercase tracking-wider text-light-muted">
                  <th className="px-4 py-3">Adapter</th>
                  <th className="px-4 py-3">支持模式</th>
                  <th className="px-4 py-3">能力</th>
                  <th className="px-4 py-3">分辨率</th>
                  <th className="px-4 py-3">时长（秒）</th>
                  <th className="px-4 py-3">状态</th>
                  <th className="px-4 py-3 text-right">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-light-border">
                {providers.map((p) => (
                  <tr
                    key={p.id}
                    className={`hover:bg-light-hover/50 ${p.isHidden ? 'opacity-60' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-light-text">{p.label}</div>
                      <div className="text-xs font-mono text-light-muted mt-0.5">{p.id}</div>
                      {p.supportedModelIds?.length > 0 && (
                        <div className="text-xs text-light-muted mt-0.5">
                          模型：{p.supportedModelIds.join(', ')}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <ModeSupportChips modeSupport={p.modeSupport} />
                    </td>
                    <td className="px-4 py-3">
                      <CapabilitySummary provider={p} />
                    </td>
                    <td className="px-4 py-3">
                      <ResolutionSummary supportedResolutions={p.supportedResolutions} />
                    </td>
                    <td className="px-4 py-3 text-light-secondary">
                      {summarizeNumbers(p.supportedDurations)}
                    </td>
                    <td className="px-4 py-3">
                      <VisibilityPill isHidden={p.isHidden} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ProviderActions
                        provider={p}
                        savingId={savingId}
                        onOpenDetail={setDetailProvider}
                        onToggleHidden={handleToggleHidden}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="divide-y divide-light-border lg:hidden">
            {providers.map((p) => (
              <div key={p.id} className={`p-4 space-y-3 ${p.isHidden ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-light-text">{p.label}</div>
                    <div className="mt-1 text-xs font-mono text-light-muted break-all">{p.id}</div>
                    {p.supportedModelIds?.length > 0 && (
                      <div className="mt-1 text-xs text-light-muted break-all">
                        模型：{p.supportedModelIds.join(', ')}
                      </div>
                    )}
                  </div>
                  <VisibilityPill isHidden={p.isHidden} />
                </div>
                <div>
                  <div className="mb-1 text-xs text-light-muted">支持模式</div>
                  <ModeSupportChips modeSupport={p.modeSupport} />
                </div>
                <div>
                  <div className="mb-1 text-xs text-light-muted">能力</div>
                  <CapabilitySummary provider={p} />
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <InfoBlock label="分辨率" value={p.supportedResolutions.join(' / ') || '—'} />
                  <InfoBlock label="时长（秒）" value={summarizeNumbers(p.supportedDurations)} />
                </div>
                <ProviderActions
                  provider={p}
                  savingId={savingId}
                  onOpenDetail={setDetailProvider}
                  onToggleHidden={handleToggleHidden}
                  mobile
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {detailProvider && (
        <ProviderDetailModal
          provider={detailProvider}
          onClose={() => setDetailProvider(null)}
        />
      )}
    </div>
  );
}

// ---------------- 子组件 ----------------

function ModeBadge({ children, color }: { children: React.ReactNode; color?: string }) {
  return (
    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${color || 'bg-light-hover text-light-secondary'}`}>
      {children}
    </span>
  );
}

function ModeSupportChips({ modeSupport }: { modeSupport: VideoProviderMetaInfo['modeSupport'] }) {
  return (
    <div className="flex flex-wrap gap-1">
      {modeSupport.text2video && <ModeBadge color="bg-blue-100 text-blue-700">文生</ModeBadge>}
      {modeSupport.img2video && <ModeBadge color="bg-emerald-100 text-emerald-700">图生</ModeBadge>}
      {modeSupport.first_last_frame && <ModeBadge color="bg-amber-100 text-amber-700">首尾帧</ModeBadge>}
      {!modeSupport.text2video && !modeSupport.img2video && !modeSupport.first_last_frame && (
        <span className="text-xs text-light-muted">—</span>
      )}
    </div>
  );
}

type CapabilityTone = 'emerald' | 'teal' | 'indigo' | 'violet';

const TONE_CLASS: Record<CapabilityTone, string> = {
  emerald: 'bg-emerald-100 text-emerald-700',
  teal: 'bg-teal-100 text-teal-700',
  indigo: 'bg-indigo-100 text-indigo-700',
  violet: 'bg-violet-100 text-violet-700',
};

function CapabilityBadge({ tone, children }: { tone: CapabilityTone; children: React.ReactNode }) {
  return (
    <span className={`px-1.5 py-0.5 rounded text-xs ${TONE_CLASS[tone]}`}>{children}</span>
  );
}

function CapabilitySummary({ provider }: { provider: VideoProviderMetaInfo }) {
  const referenceImageSummary = formatReferenceImageRule(provider);

  return (
    <>
      <div className="flex flex-wrap gap-1">
        {provider.capabilitySupport.audio && <CapabilityBadge tone="emerald">音频</CapabilityBadge>}
        {provider.capabilitySupport.audioReference && (
          <CapabilityBadge tone="teal">参考音频</CapabilityBadge>
        )}
        {provider.capabilitySupport.cameraMovement && (
          <CapabilityBadge tone="indigo">镜头</CapabilityBadge>
        )}
        {provider.capabilitySupport.motionIntensity && (
          <CapabilityBadge tone="violet">运动幅度</CapabilityBadge>
        )}
        {!provider.capabilitySupport.audio &&
          !provider.capabilitySupport.audioReference &&
          !provider.capabilitySupport.cameraMovement &&
          !provider.capabilitySupport.motionIntensity && (
            <span className="text-xs text-light-muted">—</span>
          )}
      </div>
      <div className="mt-1 flex flex-wrap gap-1">
        {referenceImageSummary && (
          <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-sky-100 text-sky-700">
            图 {referenceImageSummary}
          </span>
        )}
        {provider.maxAudioReferenceCount > 0 && (
          <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
            音 ×{provider.maxAudioReferenceCount}
          </span>
        )}
        {provider.maxVideoReferenceCount > 0 && (
          <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-rose-100 text-rose-700">
            视 ×{provider.maxVideoReferenceCount}
          </span>
        )}
      </div>
    </>
  );
}

function ResolutionSummary({ supportedResolutions }: { supportedResolutions: string[] }) {
  return (
    <div className="flex flex-wrap gap-1">
      {supportedResolutions.length > 0 ? (
        supportedResolutions.map((r) => (
          <span
            key={r}
            className="px-1.5 py-0.5 rounded text-xs bg-light-hover text-light-secondary"
          >
            {r}
          </span>
        ))
      ) : (
        <span className="text-xs text-light-muted">—</span>
      )}
    </div>
  );
}

function VisibilityPill({ isHidden }: { isHidden?: boolean }) {
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
        isHidden ? 'bg-zinc-100 text-zinc-600' : 'bg-emerald-100 text-emerald-700'
      }`}
    >
      {isHidden ? '已隐藏' : '可见'}
    </span>
  );
}

function InfoBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-light-hover p-3">
      <div className="text-xs text-light-muted">{label}</div>
      <div className="mt-1 text-sm text-light-text break-all">{value}</div>
    </div>
  );
}

function ProviderActions({
  provider,
  savingId,
  onOpenDetail,
  onToggleHidden,
  mobile = false,
}: {
  provider: VideoProviderMetaInfo;
  savingId: string | null;
  onOpenDetail: (provider: VideoProviderMetaInfo) => void;
  onToggleHidden: (provider: VideoProviderMetaInfo) => void;
  mobile?: boolean;
}) {
  return (
    <div className={mobile ? 'flex flex-wrap gap-2' : 'inline-flex items-center gap-1.5'}>
      <button
        onClick={() => onOpenDetail(provider)}
        className={mobile
          ? 'inline-flex items-center gap-1.5 rounded-lg border border-light-border px-3 py-2 text-xs text-light-secondary hover:bg-light-hover'
          : 'p-1.5 rounded-lg hover:bg-light-hover text-light-secondary'}
        title="查看能力详情"
      >
        <Info className="w-4 h-4" />
        {mobile && '详情'}
      </button>
      <button
        onClick={() => onToggleHidden(provider)}
        className={mobile
          ? 'inline-flex items-center gap-1.5 rounded-lg border border-light-border px-3 py-2 text-xs text-light-secondary hover:bg-light-hover'
          : `p-1.5 rounded-lg ${
              provider.isHidden
                ? 'hover:bg-emerald-50 text-emerald-600'
                : 'hover:bg-zinc-100 text-zinc-500'
            }`}
        title={provider.isHidden ? '恢复显示' : '隐藏 Adapter'}
        disabled={savingId === provider.id}
      >
        {savingId === provider.id ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : provider.isHidden ? (
          <Eye className="w-4 h-4" />
        ) : (
          <EyeOff className="w-4 h-4" />
        )}
        {mobile && (provider.isHidden ? '恢复显示' : '隐藏')}
      </button>
    </div>
  );
}

function summarizeNumbers(values: number[] | { type: string; values?: number[]; min?: number; max?: number; step?: number }): string {
  if (!values) return '—';
  // 新格式：DurationSpec 对象
  if (typeof values === 'object' && !Array.isArray(values)) {
    if (values.type === 'range') {
      return `${values.min} ~ ${values.max}s（步长 ${values.step}）`;
    }
    if (values.type === 'enum' && Array.isArray(values.values)) {
      return summarizeNumberArray(values.values);
    }
    return '—';
  }
  // 旧格式兼容：number[]
  return summarizeNumberArray(values);
}

function summarizeNumberArray(values: number[]): string {
  if (!values || values.length === 0) return '—';
  if (values.length <= 6) return values.join(' / ');
  const sorted = [...values].sort((a, b) => a - b);
  return `${sorted[0]} ~ ${sorted[sorted.length - 1]}（${sorted.length} 档）`;
}

interface ProviderDetailModalProps {
  provider: VideoProviderMetaInfo;
  onClose: () => void;
}

function ProviderDetailModal({ provider, onClose }: ProviderDetailModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-light-border flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-light-text">{provider.label}</h3>
            <div className="text-xs font-mono text-light-muted mt-0.5">{provider.id}</div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-light-hover text-light-secondary"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-5 text-sm">
          <DetailSection title="能力概述">
            <p className="text-light-secondary leading-relaxed">{provider.adminCapability}</p>
          </DetailSection>

          <DetailSection title="详细说明">
            <p className="text-light-secondary leading-relaxed whitespace-pre-wrap">
              {provider.adminDetail}
            </p>
          </DetailSection>

          <DetailSection title="模式支持">
            <DetailRow label="文生视频" value={booleanLabel(provider.modeSupport.text2video)} />
            <DetailRow label="图生视频" value={booleanLabel(provider.modeSupport.img2video)} />
            <DetailRow
              label="首尾帧"
              value={booleanLabel(provider.modeSupport.first_last_frame)}
            />
          </DetailSection>

          <DetailSection title="高级能力">
            <DetailRow label="音频生成" value={booleanLabel(provider.capabilitySupport.audio)} />
            <DetailRow
              label="参考音频"
              value={booleanLabel(provider.capabilitySupport.audioReference)}
            />
            <DetailRow
              label="镜头运动"
              value={booleanLabel(provider.capabilitySupport.cameraMovement)}
            />
            <DetailRow
              label="运动幅度"
              value={booleanLabel(provider.capabilitySupport.motionIntensity)}
            />
          </DetailSection>

          <DetailSection title="参数边界">
            <DetailRow
              label="支持的模型名称"
              value={provider.supportedModelIds?.length ? provider.supportedModelIds.join(' / ') : '—'}
            />
            <DetailRow
              label="支持时长"
              value={summarizeNumbers(provider.supportedDurations) + ' 秒'}
            />
            <DetailRow
              label="支持分辨率"
              value={provider.supportedResolutions.length ? provider.supportedResolutions.join(' / ') : '—'}
            />
            <DetailRow
              label="支持比例"
              value={provider.supportedAspectRatios.length ? provider.supportedAspectRatios.join(' / ') : '—'}
            />
            <DetailRow
              label={provider.allowedReferenceImageCounts?.length ? '参考图规则' : '参考图上限'}
              value={formatReferenceImageRuleDetail(provider)}
            />
            <DetailRow label="参考音频上限" value={provider.maxAudioReferenceCount ? `${provider.maxAudioReferenceCount} 个` : '不支持'} />
            <DetailRow label="参考视频上限" value={provider.maxVideoReferenceCount ? `${provider.maxVideoReferenceCount} 个` : '不支持'} />
            {provider.requiresPublicReferenceUrl !== undefined && (
              <DetailRow
                label="参考图来源"
                value={
                  provider.requiresPublicReferenceUrl
                    ? '必须是公网 URL（不接受本地存储）'
                    : '支持公网 URL / base64'
                }
              />
            )}
          </DetailSection>
        </div>

        <div className="px-6 py-3 border-t border-light-border bg-light-hover/40 flex justify-end">
          <button onClick={onClose} className="btn-secondary">
            关闭
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-xs uppercase tracking-wider text-light-muted mb-2">{title}</h4>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-light-muted">{label}</span>
      <span className="text-light-text font-medium">{value}</span>
    </div>
  );
}

function booleanLabel(v: boolean): React.ReactNode {
  return v ? <span className="text-emerald-600">支持</span> : <span className="text-zinc-400">不支持</span>;
}

function formatReferenceImageRule(provider: VideoProviderMetaInfo): string {
  const allowed = normalizeAllowedCounts(provider.allowedReferenceImageCounts);
  if (allowed.length > 0) {
    return allowed.join('/');
  }
  return provider.maxReferenceImageCount > 0 ? `×${provider.maxReferenceImageCount}` : '';
}

function formatReferenceImageRuleDetail(provider: VideoProviderMetaInfo): string {
  const allowed = normalizeAllowedCounts(provider.allowedReferenceImageCounts);
  if (allowed.length > 0) {
    return `${allowed.join(' 张或 ')} 张`;
  }
  return provider.maxReferenceImageCount > 0 ? `${provider.maxReferenceImageCount} 张` : '不支持';
}

function normalizeAllowedCounts(counts?: number[]): number[] {
  if (!counts?.length) return [];
  return Array.from(new Set(counts))
    .filter((count) => Number.isInteger(count) && count > 0)
    .sort((a, b) => a - b);
}
